import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, DragEvent } from 'react';

import {
  discardFeedbackAttachment,
  uploadFeedbackAttachment,
  type FeedbackAttachment,
} from '@/entities/feedback';
import { isApiError } from '@/shared/api';

import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS } from './labels';

export type DraftState = 'uploading' | 'ready' | 'error';

export interface DraftFile {
  key: string;
  name: string;
  size: number;
  /** An object URL of the picked image (revoked on removal), for the preview before submission. */
  previewUrl: string | null;
  state: DraftState;
  /** Machine code of the failure, translated by the view. */
  error: 'tooLarge' | 'type' | 'limit' | 'rate' | 'failed' | null;
  uploaded: FeedbackAttachment | null;
}

let counter = 0;

export function pastedName(file: File, now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const extension = file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1] || 'png';
  return `screenshot-${stamp}.${extension}`;
}

/** The images in a paste. Text-only pastes (and anything else) give an empty list. */
export function imagesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const files: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files;
}

function failure(error: unknown): DraftFile['error'] {
  if (isApiError(error)) {
    if (error.status === 413 || error.code === 'MEDIA_TOO_LARGE') return 'tooLarge';
    if (error.code === 'FEEDBACK_ATTACHMENT_TYPE_UNSUPPORTED' || error.code === 'MEDIA_EMPTY') {
      return 'type';
    }
    if (error.code === 'FEEDBACK_ATTACHMENT_LIMIT') return 'limit';
    if (error.status === 429) return 'rate';
  }
  return 'failed';
}

/**
 * Files chosen for a suggestion or a reply. Each file uploads at once into a private "waiting"
 * state on the server; the form then sends only the ids, so a suggestion is created with ALL its
 * files or not at all. Removing a file discards it on the server.
 */
export function useAttachmentDraft(max = MAX_ATTACHMENTS) {
  const [files, setFiles] = useState<DraftFile[]>([]);
  const live = useRef<DraftFile[]>([]);
  useEffect(() => {
    live.current = files;
  }, [files]);
  const seen = useRef(new WeakSet<object>());

  const patch = useCallback((key: string, change: Partial<DraftFile>) => {
    setFiles((current) => current.map((f) => (f.key === key ? { ...f, ...change } : f)));
  }, []);

  const add = useCallback(
    (picked: readonly File[], pasted = false) => {
      const room = Math.max(0, max - live.current.filter((f) => f.state !== 'error').length);
      const accepted = picked.slice(0, room);
      const rejected = picked.length - accepted.length;
      const created = accepted.map((file): DraftFile => {
        counter += 1;
        const name = pasted ? pastedName(file) : file.name;
        const tooLarge = file.size > MAX_ATTACHMENT_BYTES;
        return {
          key: `f${counter}`,
          name,
          size: file.size,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
          state: tooLarge ? 'error' : 'uploading',
          error: tooLarge ? 'tooLarge' : null,
          uploaded: null,
        };
      });
      const overflow: DraftFile[] = Array.from({ length: Math.min(rejected, 1) }, () => {
        counter += 1;
        return {
          key: `f${counter}`,
          name: '',
          size: 0,
          previewUrl: null,
          state: 'error',
          error: 'limit',
          uploaded: null,
        };
      });
      setFiles((current) => [...current.filter((f) => f.name !== ''), ...created, ...overflow]);
      accepted.forEach((file, index) => {
        const item = created[index];
        if (!item || item.state === 'error') return;
        uploadFeedbackAttachment(file, item.name).then(
          (uploaded) => {
            patch(item.key, { state: 'ready', uploaded });
          },
          (error: unknown) => {
            patch(item.key, { state: 'error', error: failure(error) });
          },
        );
      });
    },
    [max, patch],
  );

  const remove = useCallback((key: string) => {
    const item = live.current.find((f) => f.key === key);
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    if (item?.uploaded) void discardFeedbackAttachment(item.uploaded.id).catch(() => undefined);
    setFiles((current) => current.filter((f) => f.key !== key));
  }, []);

  /** Forget everything WITHOUT discarding on the server (the files were attached by the form). */
  const clear = useCallback(() => {
    for (const f of live.current) if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    setFiles([]);
  }, []);

  useEffect(
    () => () => {
      for (const f of live.current) if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    },
    [],
  );

  /** `onPaste` of the form: images are taken, plain text is left alone. */
  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      if (seen.current.has(event.nativeEvent)) return;
      const images = imagesFromClipboard(event.clipboardData);
      if (images.length === 0) return;
      seen.current.add(event.nativeEvent);
      add(images, true);
      // A paste that also carries text still pastes the text into the field.
      if (!event.clipboardData.types.includes('text/plain')) event.preventDefault();
    },
    [add],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      add(Array.from(event.dataTransfer.files));
    },
    [add],
  );

  const ready = files.filter((f) => f.state === 'ready' && f.uploaded);
  return {
    files,
    add,
    remove,
    clear,
    onPaste,
    onDrop,
    uploading: files.some((f) => f.state === 'uploading'),
    ids: ready.map((f) => (f.uploaded as FeedbackAttachment).id),
    full: files.filter((f) => f.state !== 'error').length >= max,
  };
}
